import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());
  const seq = useRef(0);

  const removeToast = useCallback((id) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message, type = "info", duration = 4500) => {
      seq.current += 1;
      const id = `${Date.now()}-${seq.current}`;
      // Keep the stack short so a burst of errors never buries the screen.
      setToasts((prev) => [...prev.slice(-3), { id, message, type, duration }]);
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => removeToast(id), duration),
        );
      }
      return id;
    },
    [removeToast],
  );

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const success = useCallback((m, d) => addToast(m, "success", d), [addToast]);
  const error = useCallback((m, d) => addToast(m, "error", d ?? 7000), [addToast]);
  const warning = useCallback((m, d) => addToast(m, "warning", d), [addToast]);
  const info = useCallback((m, d) => addToast(m, "info", d), [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastViewport toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within a ToastProvider");
  return context;
}

function ToastViewport({ toasts, onRemove }) {
  if (toasts.length === 0) return null;
  return (
    <div
      role="region"
      aria-live="polite"
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-3 bottom-3 z-[100] flex flex-col items-end gap-2 sm:inset-x-auto sm:bottom-5 sm:right-5 sm:w-[380px]"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={() => onRemove(toast.id)} />
      ))}
    </div>
  );
}

const TONES = {
  success: { icon: CheckCircle2, accent: "text-emerald-400", bar: "bg-emerald-400", edge: "border-emerald-500/35" },
  error: { icon: XCircle, accent: "text-rose-400", bar: "bg-rose-400", edge: "border-rose-500/35" },
  warning: { icon: AlertTriangle, accent: "text-amber-400", bar: "bg-amber-400", edge: "border-amber-500/35" },
  info: { icon: Info, accent: "text-cyan-400", bar: "bg-cyan-400", edge: "border-cyan-500/35" },
};

function Toast({ message, type, duration, onClose }) {
  const tone = TONES[type] || TONES.info;
  const Icon = tone.icon;

  return (
    <div
      className={`pointer-events-auto relative w-full overflow-hidden rounded-2xl border ${tone.edge} glass shadow-pop animate-slide-in-right`}
    >
      <div className="flex items-start gap-3 p-3.5">
        <Icon className={`mt-px h-[18px] w-[18px] shrink-0 ${tone.accent}`} />
        <p className="min-w-0 flex-1 break-words text-sm leading-relaxed text-hi">{message}</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss notification"
          className="-m-1 shrink-0 rounded-lg p-1 text-low transition-colors hover:bg-slate-700/40 hover:text-hi"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {duration > 0 && (
        <span
          className={`absolute bottom-0 left-0 h-0.5 ${tone.bar} opacity-70`}
          style={{ animation: `toast-countdown ${duration}ms linear forwards` }}
        />
      )}
      <style>{"@keyframes toast-countdown { from { width: 100%; } to { width: 0%; } }"}</style>
    </div>
  );
}
